import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  Output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  CSV_EXPORT_ACTION,
  DialogService,
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
  ToolbarStore,
} from 'frontier/browserkit';
import {GridOptions, RowNode} from "ag-grid-community";
import {EControlActions, EsvgFiles, IControlDataChanged, IControlObject, INITIAL_STATE_TOKEN,} from 'frontier/nucleus';
import {combineLatest, concatMap, debounceTime, mergeMap, Observable, of} from "rxjs";
import {filter, tap} from "rxjs/operators";
import {
  PrescriptionAttendanceDialogComponent
} from './prescription-attendance-dialog/prescription-attendance-dialog.component';
import {
  IPermanentOrdinance,
  IPrescriptionAttendanceDialog
} from './prescription-attendance-dialog/prescription-attendance-dialog.interface';
import {ProcessOverviewStore} from '../service/process-overview.store';
import {toObservable} from '@angular/core/rxjs-interop';
import {toBlob} from 'frontier/nucleus/src/lib/utils/to-blob';
import AppControlGuids from '../../../../api/ControlGuids';
import {IClient, IInvoice, IProcessState} from '../service/state.interface';
import {MatDialogRef} from '@angular/material/dialog';
import {AllClientListService} from "../../../../api/generated";

@Component({
  selector: 'kpi4me-clients',
  templateUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: ClientsComponent},
  ],
})
export class ClientsComponent extends TableControlComponent {
  private _dialogService: DialogService = inject(DialogService);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _clientsService: AllClientListService = inject(AllClientListService);

  private selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;
  private selectedProcess$: Observable<IProcessState> = toObservable(this.selectedProcess);
  private selectedClient: Signal<IClient> = this._processOverviewService.selectedClient;
  private selectedClient$: Observable<IClient> = toObservable(this.selectedClient);
  private selectedInvoice: Signal<IInvoice> = this._processOverviewService.selectedInvoice;
  private selectedInvoice$: Observable<IInvoice> = toObservable(this.selectedInvoice);

  override GUID: string = AppControlGuids.TAllClientList;
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Dauerverordnung abrechnen',
      controlAction: EControlActions.attestation,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.document,
      action: () => this.proofOfAttendance(),
    },
    CSV_EXPORT_ACTION
  ]);

  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    pagination: true,
    rowSelection: 'single',
  };

  @Output() switchToCarts = new EventEmitter<IControlObject>();

  constructor() {
    super();

    effect(() => {
      // restore selection after refresh
      const selectedClient = this.selectedClient();
      this.savedSelectedRowsIds = selectedClient?.obj ? [selectedClient.obj.signature] : [];
      if (!selectedClient) {
        this.gridApi?.deselectAll();
      }
    });

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged) => {
          return new Map([
            [AppControlGuids.TAllClientList, new Set([EControlActions.attendance, EControlActions.delete])],
            [AppControlGuids.TPermanentOrdinanceForClient, new Set([EControlActions.delete])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => this.refreshInfiniteCache())).subscribe(),
    );

    this.subs.add(
      this.rowSelected.pipe(
        tap((selectionEvent: RowNode[]) => {
          if (!selectionEvent[0]?.data?.apiRow) {
            return;
          }
          this._processOverviewService.selectedClient$.next(
            {obj: selectionEvent[0].data.apiRow.obj, name: selectionEvent[0].data[0]?.value},
          )
        })
      ).subscribe()
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.subs.add(
      combineLatest([
        this._toolbarStore.toolbarFilterDto$,
        this.selectedProcess$,
        this.selectedInvoice$,
        this.selectedClient$,
      ]).pipe(
        debounceTime(100),
        tap(([toolbarFilter, selectedProcess,
               selectedInvoice, selectedClient]) => {

          const {state, category} = selectedProcess;

          // Update the apiInstance and trigger the fetching of the data with the new filter.
          this.patchFilter({
            ...toolbarFilter,
            valuelist: typeof (state) === 'number' ? [state] : category ? [category] : undefined,
            typename: selectedInvoice?.invoiceId || (selectedClient ? 'client' : null),
            parent: selectedClient?.obj || null,
          })
        }),
      ).subscribe()
    );
  }

  proofOfAttendance(): Observable<any> {
    this.gridApi?.hideOverlay();
    return this._clientsService.allClientListgetRepository({
      InstanceId: this.instanceId(),
      AttributeName: 'PermanentOrdinance',
      AttributeIdx: -1,
    }).pipe(
      mergeMap((repo: IControlObject[]) => {
        const permanentOrdinanceRepo: IPermanentOrdinance[] = repo as IPermanentOrdinance[];
        const data: Partial<IPrescriptionAttendanceDialog> = {
          permanentOrdinances: permanentOrdinanceRepo
        }
        const dialogRef: MatDialogRef<PrescriptionAttendanceDialogComponent> = this._dialogService.dialog.open(
          PrescriptionAttendanceDialogComponent, {data})
        return dialogRef.afterClosed();
      }),
      filter(v => v != null),
      concatMap((dialogRes: IPrescriptionAttendanceDialog | null) => {
        return this.addAttendanceCertificate(
          dialogRes.selectedOrdinance, dialogRes.fromDate, dialogRes.toDate, dialogRes.selectedCarts
        ).pipe(
          tap(() => {
              this.controlStore.controlDataChanged$.emit({GUID: this.GUID, changeType: EControlActions.attendance});
            }
          ),
          concatMap(result => {
            return result && dialogRes.attendanceCertificate != null ?
              this.controlService.controlPostUploadDocument(
                dialogRes.attendanceCertificate?.name,
                dialogRes.attendanceCertificate?.fileFormate,
                result.rowid,
                result.type,
                result.signature,
                'document',
                toBlob(dialogRes.attendanceCertificate?.data as ArrayBuffer)
              ) : of(null)
          })
        )
      }),
    );
  }

  private addAttendanceCertificate(permanentOrdinance: IControlObject, startDate: string, endDate: string,
                                   selectedCarts: IControlObject[]): Observable<any> {
    return this._clientsService.allClientListAdAttendanceCertificate({
      InstanceId: this.instanceId(),
      PermanentOrdinance: permanentOrdinance,
      StartDate: startDate,
      EndDate: endDate,
      SelectedCarts: selectedCarts,
    })
  }
}
